import React, { useEffect, useRef, useState } from 'react';
import { announce } from '@react-aria/live-announcer';
import Toast from '../components/ui/Toast';
import { ToastItem, ToastContextType } from '../types/toast';

export const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

// Small delay before announcing. Toasts are frequently fired immediately before
// a modal closes (e.g. the "tag created" toast in AddTagModal); waiting lets the
// dialog finish its ~150ms unmount and restore focus first, so the screen reader
// isn't mid-announcing the refocused control when the message lands.
const ANNOUNCE_DELAY_MS = 250;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const announcementTimeouts = useRef<Set<number>>(new Set());

  useEffect(
    () => () => {
      announcementTimeouts.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      announcementTimeouts.current.clear();
    },
    [],
  );

  const showToast = (message: string, type: ToastItem['type'], duration = 3000) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, message, type, duration }]);

    // The visual Toast is portaled in fresh with its text already inside it (and
    // torn down a few seconds later), which screen readers never announce, so we
    // mirror each message through react-aria's announce(). It owns persistent,
    // visually-hidden assertive + polite live regions on document.body and
    // appends a fresh node per call — the technique VoiceOver actually honors.
    // Errors interrupt (assertive); everything else is polite.
    const timeoutId = window.setTimeout(() => {
      announcementTimeouts.current.delete(timeoutId);
      announce(message, type === 'error' ? 'assertive' : 'polite');
    }, ANNOUNCE_DELAY_MS);
    announcementTimeouts.current.add(timeoutId);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
}
